import { Image, ImageProps } from '@graphcommerce/image'
import { ProductListPrice } from '@graphcommerce/magento-product/components/ProductListPrice/ProductListPrice'
import { ProductListItemFragment, useProductLink } from '@graphcommerce/magento-product/index.interceptor'
import {
  responsiveVal,
  extendableComponent,
  useNumberFormat,
  breakpointVal,
} from '@graphcommerce/next-ui'
import { Trans } from '@lingui/react'
import {
  ButtonBase,
  Typography,
  Box,
  styled,
  SxProps,
  Theme,
  useEventCallback,
} from '@mui/material'
import React from 'react'
import IconPinned from '../icons/IconPinned'
import IconStopwatch from '../icons/IconStopwatch'

import ShowRateOnItem from '../showRateOnItem'
import BottomAction from './BottomActions'
import CheckIcons from './CheckIcons'
import DiscountBadge from './DiscountBadge'
import ShowSwatchColorsOnPlP from './showSwatchColorsOnPlP'


const { classes, selectors } = extendableComponent('ProductListItem', [
  'root',
  'item',
  'title',
  'titleContainer',
  'subtitle',
  'price',
  'overlayItems',
  'topLeft',
  'topRight',
  'bottomLeft',
  'bottomRight',
  'imageContainer',
  'placeholder',
  'image',
  'discount',
] as const)

export type OverlayAreaKeys = 'topLeft' | 'bottomLeft' | 'topRight' | 'bottomRight'

export type OverlayAreas = Partial<Record<OverlayAreaKeys, React.ReactNode>>

type StyleProps = {
  aspectRatio?: [number, number]
  imageOnly?: boolean
}

type BaseProps = { subTitle?: React.ReactNode; children?: React.ReactNode } & StyleProps &
  OverlayAreas &
  ProductListItemFragment &
  Pick<ImageProps, 'loading' | 'sizes' | 'dontReportWronglySizedImages'>

export type ProductListItemProps = BaseProps & {
  sx?: SxProps<Theme>
  titleComponent?: React.ElementType
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>, item: ProductListItemFragment) => void
}

const StyledImage = styled(Image)({})

export function ProductListItem(props: ProductListItemProps) {
  const {
    subTitle,
    bottomLeft,
    bottomRight,
    small_image,
    name,
    price_range,
    children,
    imageOnly = false,
    loading,
    pinned,
    stock_status,
    sizes,
    expiration_date,
    topLeft,
    topRight,
    dontReportWronglySizedImages,
    rating_summary,
    aspectRatio = [4, 3],
    titleComponent = 'h2',
    sx = [],
    onClick,
  } = props


  const handleClick = useEventCallback((e: React.MouseEvent<HTMLAnchorElement>) =>
    onClick?.(e, props),
  )

  const productLink = useProductLink(props)
  const discount = Math.floor(price_range.minimum_price.discount?.percent_off ?? 0)

  const formatter = useNumberFormat({ style: 'percent', maximumFractionDigits: 1 })



  return (
    <ButtonBase
      href={productLink}
      sx={[
        (theme) => ({
          display: 'block',
          position: 'relative',
          height: '100%',
          ...breakpointVal(
            'borderRadius',
            theme.shape.borderRadius * 2,
            theme.shape.borderRadius * 3,
            theme.breakpoints.values,
          ),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      className={`${classes.root} group/base overflow-hidden`}
      onClick={onClick ? handleClick : undefined}
    >
      <Box
        sx={(theme) => ({
          display: 'grid',
          bgcolor: 'background.image',
          ...breakpointVal(
            'borderRadius',
            theme.shape.borderRadius * 2,
            theme.shape.borderRadius * 3,
            theme.breakpoints.values,
          ),
          overflow: 'hidden',
          padding: responsiveVal(8, 12),
          '& > picture': {
            gridArea: `1 / 1 / 3 / 3`,
            margin: `calc(${responsiveVal(8, 12)} * -1)`,
          },
        })}
        className={classes.imageContainer}
      >
        {small_image ? (
          <StyledImage
            layout='fill'
            width={1}
            height={1}
            sizes={sizes}
            dontReportWronglySizedImages={dontReportWronglySizedImages}
            src={small_image.url ?? ''}
            alt={small_image.label ?? ''}
            className={classes.image}
            loading={loading}
            sx={{ objectFit: 'contain', aspectRatio: `${aspectRatio[0] / aspectRatio[1]}` }}
          />
        ) : (
          <Box
            sx={{
              gridArea: `1 / 1 / 3 / 3`,
              typography: 'caption',
              display: 'flex',
              textAlign: 'center',
              height: '100%',

              justifyContent: 'center',
              alignItems: 'center',
              color: 'background.default',
              userSelect: 'none',
            }}
            className={`${classes.placeholder} ${classes.image}`}
          >
            <Trans id='No Image' />


          </Box>
        )}

        <Box
          sx={{
            gridArea: `2/ 1 / 1 / 1`,
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column'
          }}
          className={classes.topRight}>

          <DiscountBadge {...price_range.minimum_price} />

          {pinned === 1 && <IconPinned width={26} height={26} color="#999" />}
          {expiration_date && <IconStopwatch width={26} height={26} color="red" />}
          <CheckIcons uid={props?.uid} sku={String(props?.sku)} id={String(props?.id)} />
        </Box>


        {stock_status !== "IN_STOCK" ? <Box
          sx={{
            justifySelf: 'end',
            textAlign: 'right',
            gridArea: `1 / 2 / 2 / 3`,
            padding: " 3px 7px 2px",
            maxHeight: '22px',
            color: "#fff",
            background: "#b20b51",
            zIndex: 1,
            display: "flex",
            alignItems: 'center',

          }}
          className={classes.topLeft}>ناموجود</Box> : !imageOnly && (
            <>

              <Box
                sx={{
                  justifySelf: 'end',
                  textAlign: 'right',
                  gridArea: `1 / 2 / 2 / 3`,
                  zIndex: 1,
                }}
                className={classes.topLeft}
              >


                <Box sx={{
                  position: 'relative',
                  zIndex: 1,
                }}>
                  <ShowSwatchColorsOnPlP configurable_options={topLeft?.props?.children[1]?.props?.configurable_options} />
                </Box>
              </Box>
              <Box
                sx={{
                  alignSelf: 'flex-end',
                  gridArea: `2 / 1 / 3 / 2`,
                  zIndex: 1,
                }}
                className={classes.bottomLeft}
              >
                {/* {bottomLeft} */}
              </Box>
              <Box
                sx={{
                  textAlign: 'right',
                  alignSelf: 'flex-end',
                  gridArea: `2 / 2 / 3 / 3`,
                  zIndex: 1,
                  justifySelf: 'end',
                  position: "relative"
                }}
                className={classes.bottomRight}
              >
                {/* {bottomRight} */}
              </Box>
            </>
          )}
      </Box>


      {
        !imageOnly && (
          <>
            <Box
              className={classes.titleContainer}
              sx={(theme) => ({
                display: 'grid',
                alignItems: 'baseline',
                marginTop: theme.spacings.xs,
                columnGap: 1,
                gridTemplateAreas: {
                  xs: `"title title" "subtitle price"`,
                  md: `"title subtitle price"`,
                },
                gridTemplateColumns: { xs: 'unset', md: 'auto auto 1fr' },
                justifyContent: 'space-between',
              })}
            >
              {stock_status === "IN_STOCK" ? <ProductListPrice
                {...price_range.minimum_price}
                sx={(theme) => ({
                  gridArea: 'price',
                  textAlign: 'right',
                  width: '100%',
                  color: theme.palette.primary.main,
                  fontSize: '14px',
                  justifySelf: { sm: 'flex-end' },
                })}
              /> : <p className='text-primary-500'>
                ناموجود
              </p>}
              <Typography
                component={titleComponent}
                variant='subtitle1'
                sx={{
                  display: 'inline',
                  color: '#444444',
                  overflowWrap: 'break-word',
                  maxWidth: '100%',
                  gridArea: 'title',
                  fontWeight: 'fontWeightBold',
                  textAlign: 'center'
                }}
                className={`${classes.title} truncate`}
              >
                {name}
              </Typography>
              <Box
                sx={{
                  gridArea: 'subtitle',
                  textAlign: 'center',
                  color: '#444444',
                }} className={`${classes.subtitle} truncate`}>
                {subTitle}
              </Box>

              <div className='mb-2 flex flex-row w-full  justify-between px-3'>

                <BottomAction {...(props as any)} />
                <ShowRateOnItem value={rating_summary} />
              </div>
            </Box>
            {children}
          </>
        )
      }
    </ButtonBase >
  )
}

ProductListItem.selectors = { ...selectors, ...ProductListPrice.selectors }
